// scheduleDraftPresentationModal.jsx
import { useToast } from "contexts/ToastContext";
import DynamicFormInput from "components/form/form";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingIcon } from "utils/constant";
import { createResearchSchedule, updateResearchSchedule } from "services/researchScheduleService";
import { getInternalLegalResearchRequest, getLegalResearchRequest } from "services/legalResearchService";
import { useAuth } from "contexts/AuthContext";

interface ScheduleDraftPresentationModalProps {
    data: any;
    setLegalResearch: Function;
    isOpen: boolean;
    onClose: () => void;
}

const ScheduleDraftPresentationModal: React.FC<ScheduleDraftPresentationModalProps> = ({
    data: singleData,
    setLegalResearch,
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();
    const contextData = useAuth();

    // Handle both internal (internal_topic_schdule) and external (schedules) research requests
    const proposalSchedule = singleData?.schedules?.find(
        (schedule: any) => schedule.purpose === "draft_presentation"
    ) || singleData?.internal_topic_schdule?.find(
        (schedule: any) => schedule.purpose === "draft_presentation"
    );

    const [formState, setFormState] = useState({
        location: "",
        remark: "",
        schedule_datetime: new Date().toISOString().split("T")[0],
    });

    // Update form state when proposalSchedule changes (for edit functionality)
    useEffect(() => {
        if (proposalSchedule) {
            setFormState({
                location: proposalSchedule?.location ?? "",
                remark: proposalSchedule?.remark ?? "",
                schedule_datetime: proposalSchedule?.scheduled_datetime
                    ? new Date(proposalSchedule.scheduled_datetime).toISOString().split("T")[0]
                    : new Date().toISOString().split("T")[0],
            });
        } else {
            // Reset form when there's no existing schedule
            setFormState({
                location: "",
                remark: "",
                schedule_datetime: new Date().toISOString().split("T")[0],
            });
        }
    }, [proposalSchedule]);

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    if (!isOpen) return null;

    const handleAssignSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        const data = new FormData();

        data.append("location", formState.location);
        if (singleData.requested_by === "internal")
            data.append("internal_research_request", singleData.id);
        else
            data.append("research_request", singleData.id);
        data.append("scheduled_datetime", formState.schedule_datetime);
        data.append("remark", formState.remark);
        data.append("purpose", "draft_presentation");

        try {
            let schedulesResponse;
            // Check if schedule already exists - update if exists, create if not
            if (proposalSchedule?.id) {
                // Update existing schedule
                schedulesResponse = await updateResearchSchedule(contextData.token, proposalSchedule.id, data);
            } else {
                // Create new schedule
                schedulesResponse = await createResearchSchedule(contextData.token, data);
            }
            
            let response: any;
            if (singleData.requested_by === "internal")
                response = await getInternalLegalResearchRequest(contextData.token, singleData.id);
            else
                response = await getLegalResearchRequest(singleData.id, contextData.token);

            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(
                proposalSchedule?.id 
                    ? t("ScheduleUpdatedSuccess") 
                    : t("ScheduleCreatedSuccess"), 
                "success"
            );
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest"), "error");
        } finally {
            setSubmitting(false);
        }

        onClose(); // Close modal after assignment
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <form
                onSubmit={handleAssignSubmit}
                className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 relative"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("ApprovalAndSettingAppointmentForResearchDraftPresentation")}
                </h2>

                {/* Remark Section */}
                <div className="mb-4">
                    <DynamicFormInput
                        inputs={[
                            {
                                value: formState.location,
                                type: "text",
                                label: t("Location"),
                                name: "location",
                                onChange: handleChange,
                                placeholder: t("LocationPlaceholder"),
                            },
                        ]} />
                </div>
                <label
                    htmlFor="select-department"
                    className="block text-sm font-medium text-gray-700 mb-2"
                >
                    {t("Schedule")}
                </label>
                <input
                    type="date"
                    id="schedule_datetime"
                    name="schedule_datetime"
                    className="w-full p-3 border border-gray-300 rounded-md pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                    value={formState?.schedule_datetime}
                    onChange={handleChange}
                    min={new Date().toISOString().split("T")[0]}
                    required
                />
                <label
                    htmlFor="select-department"
                    className="block mt-4 text-sm font-medium text-gray-700 mb-2"
                >
                    {t("Remark")}
                </label>
                <textarea
                    id="remark"
                    name="remark"
                    className="w-full mt-2 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                    placeholder={t("DetailHere")}
                    value={formState?.remark}
                    onChange={handleChange}
                    required
                ></textarea>

                {/* Assign Button */}
                <div className="text-center mt-6 flex gap-4">
                    <button
                        type="button"
                        onClick={() => onClose()}
                        className="bg-white hover:bg-gray-200 border border-gray-900 font-bold py-3 px-8 rounded-md w-full"
                    >
                        {t("Back")}
                    </button>
                    <button
                        type="submit"
                        className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-md w-full"
                    >
                        {submitting ? (
                            <div className="flex items-center justify-center">
                                {LoadingIcon} {t("Submitting")}
                            </div>
                        ) : (
                            t("Schedule")
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ScheduleDraftPresentationModal;